import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Button,
  Col,
  Form,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import toast from 'react-hot-toast';
import { fetchExportPremiumCare } from '../action/action';
import { PREMIUM_CARE_STATUS_OPTIONS } from '../constants';



function PremiumCareExportReport() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { afterToday, combine, allowedMaxDays } = DateRangePicker;

  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [status, setStatus] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [search, setSearch] = useState('');

  // Drafts so the user can cancel without losing the committed filters.
  const [draftStatus, setDraftStatus] = useState('');
  const [draftStoreCode, setDraftStoreCode] = useState('');
  const [draftSearch, setDraftSearch] = useState('');

  const activeFilterCount = useMemo(
    () =>
      [status, storeCode, search].filter((v) => v && String(v).length > 0).length,
    [status, storeCode, search]
  );

  const openFilterDrawer = () => {
    setDraftStatus(status);
    setDraftStoreCode(storeCode);
    setDraftSearch(search);
    setIsFilterDrawerOpen(true);
  };

  const closeFilterDrawer = () => setIsFilterDrawerOpen(false);

  const handleApplyFilters = () => {
    setStatus(draftStatus);
    setStoreCode(draftStoreCode);
    setSearch(draftSearch);
    setIsFilterDrawerOpen(false);
  };

  const handleResetFilters = () => {
    setDraftStatus('');
    setDraftStoreCode('');
    setDraftSearch('');
    setStatus('');
    setStoreCode('');
    setSearch('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }
    setLoading(true);
    try {
      const result = await fetchExportPremiumCare({
        params: {
          date_from: startDate,
          date_to: endDate || startDate,
          status,
          store_code: storeCode,
          search,
        },
        dispatch,
      });

      if (result?.code === 200) {
        toast.success(result?.message || 'Export ready');
        const link = result?.data?.[0]?.report_link;
        if (link) {
          window.location.href = link;
        }
      } else {
        toast.error(result?.message || 'Export failed. Please try again.');
      }
    } catch (error) {
      toast.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-care-export-container">
      <Form onSubmit={handleSubmit}>
        <div className="DashbordFliter d-flex flex-wrap justify-content-end align-items-center gap-2">
          <div className="InputBg">
            <DateRangePicker
              placement="leftStart"
              placeholder="Select Date Range"
              onClean={() => {
                setStartDate(moment().format('YYYY-MM-DD'));
                setEndDate(moment().format('YYYY-MM-DD'));
              }}
              disabledDate={combine(allowedMaxDays(365), afterToday())}
              onChange={(selectedDates) => {
                if (selectedDates && selectedDates.length === 2) {
                  setStartDate(moment(selectedDates[0]).format('YYYY-MM-DD'));
                  setEndDate(moment(selectedDates[1]).format('YYYY-MM-DD'));
                }
              }}
            />
          </div>

          <button
            type="button"
            className="btn btn-outline-dark d-inline-flex align-items-center gap-1"
            onClick={openFilterDrawer}
            disabled={loading}
          >
            <i className="bx bx-filter-alt"></i>
            Filters
            {activeFilterCount > 0 && (
              <Badge color="dark" pill className="ms-1">
                {activeFilterCount}
              </Badge>
            )}
          </button>

          <button
            type="submit"
            className="btn btn-dark d-flex align-items-center"
            disabled={loading}
          >
            {loading && (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            )}
            Export
            <i className="bx bx-download ms-2" style={{ fontSize: '18px' }}></i>
          </button>
        </div>
      </Form>

      <Offcanvas
        isOpen={isFilterDrawerOpen}
        toggle={closeFilterDrawer}
        direction="end"
        style={{ width: '420px' }}
      >
        <OffcanvasHeader toggle={closeFilterDrawer} className="border-bottom">
          Export Filters
        </OffcanvasHeader>
        <OffcanvasBody>
          <Row>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Status</Label>
              <Input
                type="select"
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
              >
                <option value="">All Status</option>
                {PREMIUM_CARE_STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Store Code</Label>
              <Input
                type="text"
                placeholder="e.g. DHECOMM"
                value={draftStoreCode}
                onChange={(e) => setDraftStoreCode(e.target.value)}
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label className="form-label fw-semibold">Search</Label>
              <Input
                type="text"
                placeholder="Receipt / sales order no"
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button color="success" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button outline color="danger" onClick={handleResetFilters}>
              Reset
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
}

export default PremiumCareExportReport;
